import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Send,
  Bell,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Megaphone,
  Calendar,
  Eye,
  TrendingUp,
  AlertCircle,
  Zap,
  RefreshCw,
  CheckCheck,
  AlertTriangle
} from 'lucide-react';

const BroadcastPage = () => {
  const { api } = useAuth();
  const [broadcasts, setBroadcasts] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [acknowledgements, setAcknowledgements] = useState(null);
  const [loadingAcks, setLoadingAcks] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [priority, setPriority] = useState('normal');
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [broadcastsRes, statsRes, usersRes] = await Promise.all([
        api.get('/broadcasts'),
        api.get('/broadcasts/stats'),
        api.get('/users')
      ]);
      setBroadcasts(broadcastsRes.data.broadcasts || []);
      setStats(statsRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load broadcast data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendBroadcast = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Please fill in title and message');
      return;
    }

    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        message: message.trim(),
        recipient_type: recipientType,
        priority
      };

      if (recipientType === 'selected') {
        payload.recipient_ids = selectedUsers;
      } else if (recipientType === 'role') {
        payload.recipient_role = selectedRole;
      }

      if (scheduleType === 'scheduled' && scheduledDate && scheduledTime) {
        payload.scheduled_at = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      }

      await api.post('/broadcasts', payload);
      toast.success(scheduleType === 'now' ? 'Broadcast sent!' : 'Broadcast scheduled!');
      
      // Reset form
      setTitle('');
      setMessage('');
      setRecipientType('all');
      setSelectedUsers([]);
      setSelectedRole('');
      setPriority('normal');
      setScheduleType('now');
      setScheduledDate('');
      setScheduledTime('');
      setShowCreateDialog(false);
      
      fetchData();
    } catch (error) {
      console.error('Error sending broadcast:', error);
      toast.error('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleCancelBroadcast = async (id) => {
    try {
      await api.delete(`/broadcasts/${id}`);
      toast.success('Broadcast cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel broadcast');
    }
  };

  const fetchAcknowledgements = async (broadcastId) => {
    setLoadingAcks(true);
    try {
      const response = await api.get(`/broadcasts/${broadcastId}/acknowledgements`);
      setAcknowledgements(response.data);
    } catch (error) {
      console.error('Error fetching acknowledgements:', error);
      toast.error('Failed to load acknowledgement data');
    } finally {
      setLoadingAcks(false);
    }
  };

  const handleViewAcknowledgements = (broadcast) => {
    setSelectedBroadcast(broadcast);
    fetchAcknowledgements(broadcast.id);
  };

  const getPriorityColor = (p) => {
    switch (p) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'normal': return 'bg-blue-500';
      case 'low': return 'bg-gray-500';
      default: return 'bg-blue-500';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Sent</Badge>;
      case 'scheduled':
        return <Badge className="bg-amber-500"><Clock className="w-3 h-3 mr-1" />Scheduled</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-500"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-2">
            <Megaphone className="w-8 h-8 text-indigo-600" />
            Broadcast Center
          </h1>
          <p className="text-muted-foreground mt-1">Send notifications to your team</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700">
              <Send className="w-4 h-4 mr-2" />
              New Broadcast
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-primary" />
                Create Broadcast
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Title */}
              <div>
                <label className="text-sm font-medium mb-1 block">Title</label>
                <Input
                  placeholder="Enter broadcast title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              
              {/* Message */}
              <div>
                <label className="text-sm font-medium mb-1 block">Message</label>
                <Textarea
                  placeholder="Enter your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                />
              </div>
              
              {/* Recipients */}
              <div>
                <label className="text-sm font-medium mb-1 block">Recipients</label>
                <Select value={recipientType} onValueChange={setRecipientType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select recipients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users + TV</SelectItem>
                    <SelectItem value="tv_only">TV Display Only</SelectItem>
                    <SelectItem value="role">By Role + TV</SelectItem>
                    <SelectItem value="selected">Select Users + TV</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {recipientType === 'tv_only' 
                    ? 'Broadcast will only appear on TV display for 60 seconds' 
                    : 'Broadcast will be sent to users and TV display'}
                </p>
              </div>
              
              {/* Role Selection */}
              {recipientType === 'role' && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Select Role</label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="executive">Executives</SelectItem>
                      <SelectItem value="manager">Managers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* User Selection */}
              {recipientType === 'selected' && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Select Users</label>
                  <ScrollArea className="h-40 border rounded-lg p-2">
                    {users.filter(u => u.role !== 'admin').map(user => (
                      <div key={user.id} className="flex items-center gap-2 p-2 hover:bg-muted rounded">
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedUsers([...selectedUsers, user.id]);
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                            }
                          }}
                        />
                        <span>{user.first_name} {user.last_name}</span>
                        <Badge variant="outline" className="ml-auto text-xs">{user.role}</Badge>
                      </div>
                    ))}
                  </ScrollArea>
                  <p className="text-xs text-muted-foreground mt-1">{selectedUsers.length} selected</p>
                </div>
              )}
              
              {/* Priority */}
              <div>
                <label className="text-sm font-medium mb-1 block">Priority</label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Schedule */}
              <div>
                <label className="text-sm font-medium mb-1 block">When to Send</label>
                <Select value={scheduleType} onValueChange={setScheduleType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="now">Send Immediately</SelectItem>
                    <SelectItem value="scheduled">Schedule for Later</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {scheduleType === 'scheduled' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Date</label>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Time</label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
              
              <Button 
                onClick={handleSendBroadcast} 
                disabled={sending}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600"
              >
                {sending ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {scheduleType === 'now' ? 'Send Now' : 'Schedule'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-500/10 to-purple-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-500/20">
                <Megaphone className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_broadcasts || 0}</p>
                <p className="text-xs text-muted-foreground">Total Broadcasts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500/10 to-emerald-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.sent || 0}</p>
                <p className="text-xs text-muted-foreground">Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500/10 to-orange-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.scheduled || 0}</p>
                <p className="text-xs text-muted-foreground">Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg bg-gradient-to-br from-cyan-500/10 to-blue-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <Eye className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_reads || 0}</p>
                <p className="text-xs text-muted-foreground">Total Reads</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Broadcasts List */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Broadcast History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {broadcasts.length === 0 ? (
            <div className="text-center py-12">
              <Megaphone className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No broadcasts yet</p>
              <p className="text-sm text-muted-foreground/70">Create your first broadcast to notify your team</p>
            </div>
          ) : (
            <div className="space-y-3">
              {broadcasts.map((broadcast) => (
                <div 
                  key={broadcast.id} 
                  className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full ${getPriorityColor(broadcast.priority)}`} />
                        <h3 className="font-semibold truncate">{broadcast.title}</h3>
                        {getStatusBadge(broadcast.status)}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{broadcast.message}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {broadcast.recipient_count} recipients
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {broadcast.read_count} reads
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(broadcast.sent_at || broadcast.scheduled_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {broadcast.status === 'sent' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewAcknowledgements(broadcast)}
                          className="text-indigo-600 hover:text-indigo-700"
                          data-testid={`view-acks-${broadcast.id}`}
                        >
                          <CheckCheck className="w-4 h-4 mr-1" />
                          View Acks
                        </Button>
                      )}
                      {broadcast.status === 'scheduled' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleCancelBroadcast(broadcast.id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Acknowledgements Dialog */}
      <Dialog open={selectedBroadcast !== null} onOpenChange={(open) => !open && setSelectedBroadcast(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCheck className="w-5 h-5 text-primary" />
              Acknowledgement Status
            </DialogTitle>
          </DialogHeader>
          
          {loadingAcks ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : acknowledgements ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <p className="text-2xl font-bold text-blue-600">{acknowledgements.total_recipients}</p>
                  <p className="text-xs text-muted-foreground">Total Recipients</p>
                </div>
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <p className="text-2xl font-bold text-green-600">{acknowledgements.total_acknowledged}</p>
                  <p className="text-xs text-muted-foreground">Acknowledged</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-2xl font-bold text-amber-600">{acknowledgements.total_pending}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
              
              {/* Tabs for Acknowledged/Pending */}
              <Tabs defaultValue="acknowledged" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="acknowledged" className="gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Acknowledged ({acknowledgements.total_acknowledged})
                  </TabsTrigger>
                  <TabsTrigger value="pending" className="gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Pending ({acknowledgements.total_pending})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="acknowledged">
                  <ScrollArea className="h-[300px] border rounded-lg p-2">
                    {acknowledgements.acknowledged?.length > 0 ? (
                      <div className="space-y-2">
                        {acknowledgements.acknowledged.map((ack) => (
                          <div key={ack.id} className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
                            <Avatar className="w-10 h-10">
                              <AvatarFallback className="bg-green-500 text-white">
                                {ack.user_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{ack.user_name}</p>
                              <p className="text-xs text-muted-foreground">{ack.user_email}</p>
                            </div>
                            <div className="text-right">
                              <CheckCircle className="w-4 h-4 text-green-500 mb-1" />
                              <p className="text-[10px] text-muted-foreground">
                                {new Date(ack.acknowledged_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <p>No acknowledgements yet</p>
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
                
                <TabsContent value="pending">
                  <ScrollArea className="h-[300px] border rounded-lg p-2">
                    {acknowledgements.pending?.length > 0 ? (
                      <div className="space-y-2">
                        {acknowledgements.pending.map((user) => (
                          <div key={user.user_id} className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
                            <Avatar className="w-10 h-10">
                              <AvatarFallback className="bg-amber-500 text-white">
                                {user.user_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{user.user_name}</p>
                              <p className="text-xs text-muted-foreground">{user.user_email}</p>
                            </div>
                            <div className="text-right">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                              <p className="text-[10px] text-amber-600 font-medium">Pending</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                          <p className="text-green-600 font-medium">All recipients acknowledged!</p>
                        </div>
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BroadcastPage;
